import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { formatBC } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, ImageIcon, Truck, CheckCircle2, X, Package, Tag, PackagePlus, Ticket } from "lucide-react";

// ---- helpers -----------------------------------------------------------------
const nowLocalInput = () => {
  // Format for <input type="datetime-local"> in browser-local TZ
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const plusDaysLocalInput = (days) => {
  const d = new Date(Date.now() + days * 86400_000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (v) => (v ? new Date(v).toISOString() : "");
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtPct = (x) => `${(Number(x || 0) * 100).toFixed(0)}%`;

function ProductForm({ initial, categories, onSave, onCancel, busy, isSuper }) {
  const [form, setForm] = useState(
    initial
      ? { image_data: "", shipping_fee_bc: 0, ...initial, product_cost_bc: initial.product_cost_bc ?? "" }
      : { name: "", description: "", image_url: "", image_data: "", price_bc: 100, product_cost_bc: "", stock: 10, shipping_fee_bc: 0, category: "uncategorized", subcategory: "", status: "active", points_redeemable: true }
  );

  // Per-product promo & shipping-tier local state (initialized from `initial`).
  const [promoOn, setPromoOn] = useState(!!initial?.promotional_sale);
  const [promo, setPromo] = useState(() => {
    const p = initial?.promotional_sale || null;
    return {
      name: p?.name || "",
      discount_pct_display: p ? Math.round(Number(p.discount_pct) * 100) : 10,
      starts_at: p?.starts_at ? isoToLocalInput(p.starts_at) : nowLocalInput(),
      ends_at: p?.ends_at ? isoToLocalInput(p.ends_at) : plusDaysLocalInput(7),
      active: p ? !!p.active : true,
    };
  });
  const [tiers, setTiers] = useState(() =>
    (initial?.shipping_tiers || []).map((t) => ({
      min_qty: Number(t.min_qty) || 1,
      free: !!t.free,
      fee_per_item_bc: Number(t.fee_per_item_bc) || 0,
    }))
  );
  const addTier = () => setTiers((prev) => [...prev, { min_qty: (prev.at(-1)?.min_qty || 1) + 5, free: false, fee_per_item_bc: 0 }]);
  const updTier = (idx, patch) => setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const rmTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const imgFileRef = React.useRef(null);

  const onImageFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) return toast.error("Image must be PNG, JPG, or WEBP.");
    if (file.size > 3 * 1024 * 1024) return toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Max 3MB.`);
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, image_data: String(r.result), image_url: "" }));
    r.readAsDataURL(file);
  };

  const activeCat = useMemo(
    () => categories.find((c) => c.slug === form.category) || null,
    [categories, form.category]
  );

  const handleCategoryChange = (e) => {
    const next = e.target.value;
    const cat = categories.find((c) => c.slug === next);
    const stillValid = cat && form.subcategory && cat.subcategories.some((s) => s.name === form.subcategory);
    setForm({ ...form, category: next, subcategory: stillValid ? form.subcategory : "" });
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      price_bc: Number(form.price_bc),
      stock: Number(form.stock),
      shipping_fee_bc: Number(form.shipping_fee_bc || 0),
      points_redeemable: !!form.points_redeemable,
    };
    // Product cost is super-admin-only. Only include if the user is super
    // AND provided a value (empty string = "leave as-is / default to 50%").
    if (isSuper) {
      if (form.product_cost_bc === "" || form.product_cost_bc === null || form.product_cost_bc === undefined) {
        // Explicit null tells the backend "no override, use 50% fallback".
        payload.product_cost_bc = null;
      } else {
        const c = Number(form.product_cost_bc);
        if (Number.isNaN(c) || c < 0) throw new Error("Product cost must be a non-negative number.");
        if (c >= Number(form.price_bc)) throw new Error("Product cost must be less than retail price (otherwise net earnings would be zero or negative).");
        payload.product_cost_bc = c;
      }
    } else {
      // Sub-admins should never send this key.
      delete payload.product_cost_bc;
    }
    // Promo — send full object when enabled, otherwise null (server clears it).
    if (promoOn) {
      if (!promo.name.trim()) throw new Error("Sale name is required when a promo is enabled.");
      const s = new Date(promo.starts_at);
      const e = new Date(promo.ends_at);
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) throw new Error("Sale end must be after sale start.");
      const pct = Number(promo.discount_pct_display) / 100;
      if (pct <= 0 || pct > 0.9) throw new Error("Discount must be between 1% and 90%.");
      payload.promotional_sale = {
        name: promo.name.trim(),
        discount_pct: pct,
        starts_at: localInputToIso(promo.starts_at),
        ends_at: localInputToIso(promo.ends_at),
        active: !!promo.active,
      };
    } else {
      payload.promotional_sale = null;
    }
    // Shipping tiers — normalize + sort by min_qty
    const cleanTiers = tiers
      .map((t) => ({
        min_qty: Math.max(1, parseInt(t.min_qty, 10) || 1),
        free: !!t.free,
        fee_per_item_bc: t.free ? 0 : Math.max(0, Number(t.fee_per_item_bc) || 0),
      }))
      .sort((a, b) => a.min_qty - b.min_qty);
    payload.shipping_tiers = cleanTiers.length ? cleanTiers : null;
    return payload;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    try {
      onSave(buildPayload());
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  return (
    <form onSubmit={onSubmit} className="glass-card gold-border p-5 space-y-3" data-testid="product-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold">{initial?.id ? "Edit Product" : "List New Product"}</h3>
        <button type="button" onClick={onCancel} className="text-white/60 hover:text-white"><X /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="zfd-label">Name</label>
          <input required className="zfd-input" value={form.name} onChange={upd("name")} data-testid="pf-name" />
        </div>
        <div>
          <label className="zfd-label">Category</label>
          <select
            className="zfd-input"
            value={form.category}
            onChange={handleCategoryChange}
            data-testid="pf-category"
          >
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="zfd-label">Subcategory</label>
          <select
            className="zfd-input disabled:opacity-40"
            value={form.subcategory}
            onChange={upd("subcategory")}
            disabled={!activeCat || activeCat.subcategories.length === 0}
            data-testid="pf-subcategory"
          >
            <option value="">— None —</option>
            {activeCat?.subcategories.map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="zfd-label">Price (BC)</label>
          <input required type="number" min={1} step="0.01" className="zfd-input" value={form.price_bc} onChange={upd("price_bc")} data-testid="pf-price" />
        </div>
        {isSuper && (() => {
          // Live net-earnings preview. Effective cost = explicit value or
          // price × 0.5 fallback (200% markup target). Confidential — this
          // block is rendered ONLY for the super-admin.
          const retail = Number(form.price_bc) || 0;
          const explicit = form.product_cost_bc === "" || form.product_cost_bc === null || form.product_cost_bc === undefined
            ? null
            : Number(form.product_cost_bc);
          const effective = explicit === null || Number.isNaN(explicit) ? retail * 0.5 : explicit;
          const netPerUnit = retail - effective;
          const marginPct = retail > 0 ? (netPerUnit / retail) * 100 : 0;
          const usingDefault = explicit === null;
          const marginColor =
            marginPct >= 50 ? "text-emerald-300" :
            marginPct >= 30 ? "text-[#FBBF24]" :
            "text-[#FCA5A5]";
          return (
            <div data-testid="pf-cost-block">
              <label className="zfd-label flex items-center justify-between">
                <span>Product Cost (BC) <span className="normal-case tracking-normal text-white/40 text-[10px] ml-1">Super-admin only · confidential</span></span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="zfd-input"
                value={form.product_cost_bc}
                onChange={upd("product_cost_bc")}
                placeholder={`auto: ₱${formatBC(retail * 0.5)} (50% of retail)`}
                data-testid="pf-product-cost"
              />
              <div className="text-[10px] text-white/45 mt-1 leading-snug">
                {usingDefault
                  ? <>Leave blank to auto-cost at <strong>50% of retail</strong> (target: retail = 2× cost).</>
                  : <>Explicit cost locked in. Clear the field to fall back to the 50% default.</>}
                <span className="block mt-1">
                  Net per unit:{" "}
                  <strong className={`font-mono-num ${marginColor}`} data-testid="pf-cost-preview">
                    ₱{formatBC(netPerUnit)} · {marginPct.toFixed(1)}% margin
                  </strong>
                </span>
              </div>
            </div>
          );
        })()}
        <div>
          <label className="zfd-label">Stock</label>
          <input required type="number" min={0} step="1" className="zfd-input" value={form.stock} onChange={upd("stock")} data-testid="pf-stock" />
        </div>
        <div>
          <label className="zfd-label">Shipping Fee per item (BC)</label>
          <input type="number" min={0} step="0.01" className="zfd-input" value={form.shipping_fee_bc} onChange={upd("shipping_fee_bc")} data-testid="pf-shipping" placeholder="0.00" />
          <div className="text-[10px] text-white/45 mt-1">Multiplied by qty at checkout. Set to <strong>0</strong> to disable.</div>
        </div>
      </div>
      <div>
        <label className="zfd-label flex items-center justify-between">
          <span>Product Image</span>
          <span className="text-white/40 text-[10px] normal-case tracking-normal">Upload preferred · PNG/JPG/WEBP · max 3MB</span>
        </label>
        <input ref={imgFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onImageFile(e.target.files?.[0])} data-testid="pf-image-file" />
        <div className="flex items-start gap-3">
          {(form.image_data || form.image_url) ? (
            <div className="relative rounded border border-white/10 overflow-hidden shrink-0" data-testid="pf-image-preview">
              <img
                src={form.image_data || (form.image_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${form.image_url}` : form.image_url)}
                alt="product preview"
                className="w-32 h-32 object-cover bg-black/40"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <button type="button" onClick={() => setForm((f) => ({ ...f, image_data: "", image_url: "" }))} className="absolute top-1 right-1 bg-black/70 hover:bg-[#DC2626] text-white rounded p-1" data-testid="pf-image-clear">
                <X size={11} />
              </button>
            </div>
          ) : null}
          <div className="flex-1 space-y-2">
            <button type="button" onClick={() => imgFileRef.current?.click()} className="w-full border-2 border-dashed border-white/15 hover:border-[#FBBF24]/60 rounded-md py-3 flex items-center justify-center gap-2 text-xs text-white/75" data-testid="pf-image-pick">
              <ImageIcon size={14} className="text-[#FBBF24]" /> Upload product image
            </button>
            <div className="text-[10px] text-white/45 flex items-center gap-2">
              <span>or paste an external URL:</span>
            </div>
            <input className="zfd-input" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value, image_data: "" }))} placeholder="https://…" data-testid="pf-image-url" />
          </div>
        </div>
      </div>
      <div>
        <label className="zfd-label">Description</label>
        <textarea rows={3} className="zfd-input" value={form.description} onChange={upd("description")} data-testid="pf-desc" />
      </div>
      <div>
        <label className="zfd-label">Status</label>
        <select className="zfd-input" value={form.status} onChange={upd("status")} data-testid="pf-status">
          <option value="active">Active</option>
          <option value="inactive">Inactive (hidden from shop)</option>
        </select>
      </div>
      <label className="flex items-start gap-2 cursor-pointer p-3 rounded border border-white/10 bg-black/25 select-none" data-testid="pf-points-redeemable-label">
        <input
          type="checkbox"
          checked={!!form.points_redeemable}
          onChange={(e) => setForm({ ...form, points_redeemable: e.target.checked })}
          className="accent-[#FBBF24] mt-0.5"
          data-testid="pf-points-redeemable"
        />
        <div className="text-xs">
          <div className="font-bold text-white">Loyalty points redeemable on this item</div>
          <div className="text-white/55 mt-0.5">Un-check for <strong className="text-[#FCA5A5]">low-margin</strong> items so operators can&apos;t burn points against your cost of goods (subject to the 30% cart cap regardless).</div>
        </div>
      </label>

      {/* ------------------------------------------------------------------- */}
      {/* Per-product Promotional Sale                                         */}
      {/* ------------------------------------------------------------------- */}
      <div className="rounded-lg border border-[#FBBF24]/25 bg-[#FBBF24]/[0.03] p-3 space-y-3" data-testid="pf-promo-block">
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={promoOn}
            onChange={(e) => setPromoOn(e.target.checked)}
            className="accent-[#FBBF24] mt-0.5"
            data-testid="pf-promo-enable"
          />
          <div className="text-xs">
            <div className="font-bold text-white flex items-center gap-1.5"><Tag size={12} className="text-[#FBBF24]" /> Item-level Promotional Sale</div>
            <div className="text-white/55 mt-0.5">Runs alongside store-wide campaigns — the <strong>best</strong> discount wins for shoppers.</div>
          </div>
        </label>
        {promoOn && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1" data-testid="pf-promo-form">
            <div>
              <label className="zfd-label">Sale Name</label>
              <input className="zfd-input" value={promo.name} onChange={(e) => setPromo({ ...promo, name: e.target.value })} placeholder="e.g. Payday Sale" data-testid="pf-promo-name" />
            </div>
            <div>
              <label className="zfd-label">Discount %</label>
              <input type="number" min={1} max={90} step={1} className="zfd-input" value={promo.discount_pct_display} onChange={(e) => setPromo({ ...promo, discount_pct_display: e.target.value })} data-testid="pf-promo-pct" />
            </div>
            <div>
              <label className="zfd-label">Starts</label>
              <input type="datetime-local" className="zfd-input" value={promo.starts_at} onChange={(e) => setPromo({ ...promo, starts_at: e.target.value })} data-testid="pf-promo-start" />
            </div>
            <div>
              <label className="zfd-label">Ends</label>
              <input type="datetime-local" className="zfd-input" value={promo.ends_at} onChange={(e) => setPromo({ ...promo, ends_at: e.target.value })} data-testid="pf-promo-end" />
            </div>
            <label className="flex items-center gap-2 md:col-span-2 text-xs">
              <input type="checkbox" className="accent-[#FBBF24]" checked={!!promo.active} onChange={(e) => setPromo({ ...promo, active: e.target.checked })} data-testid="pf-promo-active" />
              <span className="text-white/75">Active (auto-activates within the date window, deactivates after end)</span>
            </label>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* Bulk Shipping Tiers                                                  */}
      {/* ------------------------------------------------------------------- */}
      <div className="rounded-lg border border-white/10 bg-black/25 p-3 space-y-3" data-testid="pf-tier-block">
        <div className="flex items-center justify-between">
          <div className="text-xs">
            <div className="font-bold text-white flex items-center gap-1.5"><PackagePlus size={12} className="text-[#FBBF24]" /> Bulk Shipping Tiers <span className="text-white/40">(product-level)</span></div>
            <div className="text-white/55 mt-0.5">Cart triggers highest matching tier. Lower fee always wins vs. base shipping (₱{formatBC(form.shipping_fee_bc || 0)}/item).</div>
          </div>
          <button type="button" onClick={addTier} className="text-xs px-2 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1" data-testid="pf-tier-add"><Plus size={12} /> Add tier</button>
        </div>
        {tiers.length === 0 ? (
          <div className="text-[11px] text-white/40 italic">No tiers — base shipping is applied for every quantity.</div>
        ) : (
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-black/30 border border-white/5 rounded px-2 py-2" data-testid={`pf-tier-row-${i}`}>
                <span className="text-white/50 shrink-0">Qty ≥</span>
                <input type="number" min={1} className="zfd-input w-20" value={t.min_qty} onChange={(e) => updTier(i, { min_qty: e.target.value })} data-testid={`pf-tier-qty-${i}`} />
                <span className="text-white/50">→</span>
                <label className="flex items-center gap-1 text-white/70">
                  <input type="checkbox" className="accent-[#FBBF24]" checked={!!t.free} onChange={(e) => updTier(i, { free: e.target.checked })} data-testid={`pf-tier-free-${i}`} /> Free
                </label>
                {!t.free && (
                  <>
                    <span className="text-white/50">or ₱</span>
                    <input type="number" min={0} step="0.01" className="zfd-input w-24" value={t.fee_per_item_bc} onChange={(e) => updTier(i, { fee_per_item_bc: e.target.value })} data-testid={`pf-tier-fee-${i}`} />
                    <span className="text-white/50">/item</span>
                  </>
                )}
                <button type="button" onClick={() => rmTier(i)} className="ml-auto text-[#FCA5A5] hover:text-white bg-white/5 hover:bg-[#DC2626] px-1.5 py-1 rounded" data-testid={`pf-tier-rm-${i}`}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button disabled={busy} type="submit" className="btn-deploy w-full disabled:opacity-50" data-testid="pf-submit">
        {busy ? "Saving…" : initial?.id ? "Save Changes" : "Publish Product"}
      </button>
    </form>
  );
}

const ORDER_STATUS = {
  paid: { cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40", label: "Paid" },
  shipped: { cls: "bg-blue-500/15 text-blue-300 border-blue-500/40", label: "Shipped" },
  delivered: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "Delivered" },
  cancelled: { cls: "bg-[#DC2626]/15 text-[#FCA5A5] border-[#DC2626]/40", label: "Cancelled" },
};

export default function AdminStorefront() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin";
  const [section, setSection] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shopSettings, setShopSettings] = useState({ hidden_categories: [] });
  const [promotions, setPromotions] = useState([]);
  const [cartTiers, setCartTiers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(null);
  const [shipFor, setShipFor] = useState(null);
  const [tracking, setTracking] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const load = async () => {
    const [p, o, c, s, promos, ctiers, cps] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/orders"),
      api.get("/categories?include_hidden=1"),
      api.get("/admin/shop-settings"),
      api.get("/admin/promotions"),
      api.get("/admin/cart-shipping-tiers"),
      api.get("/admin/coupons"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setCategories(c.data?.categories || []);
    setShopSettings(s.data || { hidden_categories: [] });
    setPromotions(promos.data || []);
    setCartTiers(ctiers.data?.tiers || []);
    setCoupons(cps.data || []);
  };
  useEffect(() => { load(); }, []);

  const toggleCategoryVisibility = async (slug) => {
    const hidden = new Set(shopSettings.hidden_categories || []);
    if (hidden.has(slug)) hidden.delete(slug); else hidden.add(slug);
    setBusy(`cat-${slug}`);
    try {
      const { data } = await api.patch("/admin/shop-settings", { hidden_categories: [...hidden] });
      setShopSettings(data);
      toast.success(hidden.has(slug) ? "Category hidden from storefront" : "Category is now visible on storefront");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const save = async (data) => {
    setBusy("save");
    try {
      if (editing?.id) await api.patch(`/admin/products/${editing.id}`, data);
      else await api.post("/admin/products", data);
      toast.success("Product saved");
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    setBusy(p.id);
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const ship = async () => {
    if (!shipFor || !tracking.trim()) return;
    setBusy(shipFor.id);
    try {
      await api.post(`/admin/orders/${shipFor.id}/ship`, { tracking_reference: tracking.trim() });
      toast.success("Marked shipped");
      setShipFor(null); setTracking("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const deliver = async (o) => {
    setBusy(o.id);
    try { await api.post(`/admin/orders/${o.id}/deliver`); toast.success("Marked delivered"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const cancel = async (o) => {
    if (!window.confirm(`Cancel order ${o.id.slice(0,8)} and refund ${formatBC(o.total_bc)} BC?`)) return;
    setBusy(o.id);
    try { await api.post(`/admin/orders/${o.id}/cancel`); toast.success("Cancelled & refunded"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  // ---- Promotions -----------------------------------------------------------
  const savePromo = async (data) => {
    setBusy("promo-save");
    try {
      if (editingPromo?.id) await api.patch(`/admin/promotions/${editingPromo.id}`, data);
      else await api.post("/admin/promotions", data);
      toast.success("Promotion saved");
      setShowPromoForm(false);
      setEditingPromo(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };
  const delPromo = async (p) => {
    if (!window.confirm(`Delete promotion "${p.name}"?`)) return;
    setBusy(p.id);
    try { await api.delete(`/admin/promotions/${p.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };
  const togglePromo = async (p) => {
    setBusy(p.id);
    try { await api.patch(`/admin/promotions/${p.id}`, { active: !p.active }); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  // ---- Cart-level shipping tiers -------------------------------------------
  const saveCartTiers = async (nextTiers) => {
    setBusy("cart-tiers");
    try {
      const { data } = await api.put("/admin/cart-shipping-tiers", {
        tiers: nextTiers.map((t) => ({
          min_qty: Math.max(1, parseInt(t.min_qty, 10) || 1),
          free: !!t.free,
          fee_per_item_bc: t.free ? 0 : Math.max(0, Number(t.fee_per_item_bc) || 0),
        })),
      });
      setCartTiers(data.tiers || []);
      toast.success("Cart shipping tiers saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  // ---- Coupons --------------------------------------------------------------
  const saveCoupon = async (data) => {
    setBusy("coupon-save");
    try {
      if (editingCoupon?.id) await api.patch(`/admin/coupons/${editingCoupon.id}`, data);
      else await api.post("/admin/coupons", data);
      toast.success("Coupon saved");
      setShowCouponForm(false);
      setEditingCoupon(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };
  const delCoupon = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?\n(Redemption history is preserved for audit.)`)) return;
    setBusy(c.id);
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };
  const toggleCoupon = async (c) => {
    setBusy(c.id);
    try { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-storefront-panel">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setSection("products")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "products" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-products">Products ({products.length})</button>
        <button onClick={() => setSection("orders")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "orders" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-orders">Orders ({orders.length})</button>
        <button onClick={() => setSection("promotions")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "promotions" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-promotions">Promotions ({promotions.length})</button>
        <button onClick={() => setSection("coupons")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "coupons" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-coupons">Coupons ({coupons.length})</button>
        <button onClick={() => setSection("cart-shipping")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "cart-shipping" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-cart-shipping">Cart Shipping</button>
        <button onClick={() => setSection("shop-ui")} className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-md ${section === "shop-ui" ? "bg-[#FBBF24] text-[#0F2042]" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid="store-tab-shop-ui">Shop UI</button>
        <div className="ml-auto">
          {section === "products" && !showForm && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-deploy text-xs flex items-center gap-2" data-testid="new-product-btn"><Plus size={14} /> New Product</button>
          )}
          {section === "promotions" && !showPromoForm && (
            <button onClick={() => { setEditingPromo(null); setShowPromoForm(true); }} className="btn-deploy text-xs flex items-center gap-2" data-testid="new-promo-btn"><Plus size={14} /> New Promotion</button>
          )}
          {section === "coupons" && !showCouponForm && (
            <button onClick={() => { setEditingCoupon(null); setShowCouponForm(true); }} className="btn-deploy text-xs flex items-center gap-2" data-testid="new-coupon-btn"><Plus size={14} /> New Coupon</button>
          )}
        </div>
      </div>

      {section === "products" && showForm && (
        <ProductForm initial={editing} categories={categories} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} busy={busy === "save"} isSuper={isSuper} />
      )}

      {section === "products" && !showForm && (
        <div className="glass-card p-3">
          {/* Category filter chips for admin — quickly find products in a bucket */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-3 border-b border-white/5 mb-2" data-testid="admin-product-filter">
              <FilterChip active={productFilter === "all"} onClick={() => setProductFilter("all")} testid="admin-filter-all">
                All <span className="font-mono-num opacity-70 ml-1">{products.length}</span>
              </FilterChip>
              {categories.map((c) => {
                const n = products.filter((p) => p.category === c.slug).length;
                if (n === 0 && c.slug !== "uncategorized") return null;
                return (
                  <FilterChip key={c.slug} active={productFilter === c.slug} onClick={() => setProductFilter(c.slug)} testid={`admin-filter-${c.slug}`}>
                    {c.label} <span className="font-mono-num opacity-70 ml-1">{n}</span>
                  </FilterChip>
                );
              })}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                  <th className="text-left p-2">Product</th>
                  <th className="text-left">Category</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Stock</th>
                  <th className="text-right">Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filtered = productFilter === "all" ? products : products.filter((p) => p.category === productFilter);
                  if (filtered.length === 0) {
                    return (
                      <tr><td colSpan={6} className="text-center text-white/40 py-8" data-testid="no-products">
                        {productFilter === "all" ? "No products yet — list your first item." : "No products in this category."}
                      </td></tr>
                    );
                  }
                  return filtered.map((p) => (
                  <tr key={p.id} className="border-t border-white/5" data-testid={`admin-product-${p.id}`}>
                    <td className="p-2 flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-[#0a1526] overflow-hidden flex items-center justify-center">
                        {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <ImageIcon className="text-white/25" size={16} />}
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="text-[10px] text-white/45">{p.id.slice(0,8)}</div>
                      </div>
                    </td>
                    <td className="text-xs text-white/70">
                      <div>{p.category_label || p.category}</div>
                      {p.subcategory && <div className="text-[10px] text-white/45">{p.subcategory}</div>}
                    </td>
                    <td className="text-right font-mono-num text-[#FBBF24]">₱{formatBC(p.price_bc)}</td>
                    <td className="text-right font-mono-num">{p.stock}</td>
                    <td className="text-right">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/55"}`}>{p.status}</span>
                    </td>
                    <td className="text-right space-x-1">
                      <button onClick={() => { setEditing(p); setShowForm(true); }} className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-xs" data-testid={`edit-product-${p.id}`}><Edit2 size={12} /></button>
                      <button onClick={() => del(p)} disabled={busy === p.id} className="bg-[#DC2626] hover:bg-red-700 px-2 py-1 rounded text-xs disabled:opacity-40" data-testid={`del-product-${p.id}`}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {section === "orders" && (
        <div className="glass-card p-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                  <th className="text-left p-2">Placed</th>
                  <th className="text-left">Customer</th>
                  <th className="text-left">Product</th>
                  <th className="text-left">Ship To</th>
                  <th className="text-left">Tracking</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={8} className="text-center text-white/40 py-8" data-testid="no-orders">No orders.</td></tr>}
                {orders.map((o) => {
                  const S = ORDER_STATUS[o.status] || ORDER_STATUS.paid;
                  return (
                    <tr key={o.id} className="border-t border-white/5" data-testid={`admin-order-${o.id}`}>
                      <td className="p-2 text-xs text-white/70">{new Date(o.placed_at).toLocaleString()}</td>
                      <td className="text-xs">{o.user_name}<br/><span className="text-white/45">{o.user_email}</span></td>
                      <td className="text-xs">{o.product_name}<br/><span className="text-white/45">× {o.qty}</span></td>
                      <td className="text-xs max-w-[180px]">
                        <div className="truncate">{o.shipping_address}</div>
                        <div className="text-white/45">{o.contact_number}</div>
                      </td>
                      <td className="font-mono-num text-xs">{o.tracking_reference || <span className="text-white/30">—</span>}</td>
                      <td className="text-right font-mono-num text-[#FBBF24]">₱{formatBC(o.total_bc)}</td>
                      <td className="text-right">
                        <span className={`inline-flex items-center text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${S.cls}`}>{S.label}</span>
                      </td>
                      <td className="text-right space-x-1">
                        {o.status === "paid" && (
                          <button onClick={() => { setShipFor(o); setTracking(""); }} className="bg-blue-600 hover:bg-blue-500 px-2 py-1 rounded text-xs font-bold" data-testid={`ship-order-${o.id}`}><Truck size={12} /></button>
                        )}
                        {(o.status === "paid" || o.status === "shipped") && (
                          <>
                            <button onClick={() => deliver(o)} disabled={busy === o.id} className="bg-emerald-600 hover:bg-emerald-500 px-2 py-1 rounded text-xs font-bold disabled:opacity-40" data-testid={`deliver-order-${o.id}`}><CheckCircle2 size={12} /></button>
                            <button onClick={() => cancel(o)} disabled={busy === o.id} className="bg-[#DC2626] hover:bg-red-700 px-2 py-1 rounded text-xs font-bold disabled:opacity-40" data-testid={`cancel-order-${o.id}`}><X size={12} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {section === "promotions" && (
        <div className="space-y-3" data-testid="promotions-panel">
          {showPromoForm && (
            <PromotionForm
              initial={editingPromo}
              categories={categories}
              products={products}
              onSave={savePromo}
              onCancel={() => { setShowPromoForm(false); setEditingPromo(null); }}
              busy={busy === "promo-save"}
            />
          )}
          <div className="glass-card p-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                    <th className="text-left p-2">Sale Name</th>
                    <th className="text-left">Scope</th>
                    <th className="text-right">Discount</th>
                    <th className="text-left">Window</th>
                    <th className="text-right">Live</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-white/40 py-8" data-testid="no-promotions">
                      No promotions yet — launch your first sale event (e.g. 08.08 Sale).
                    </td></tr>
                  )}
                  {promotions.map((p) => (
                    <tr key={p.id} className="border-t border-white/5" data-testid={`promo-row-${p.id}`}>
                      <td className="p-2 font-semibold text-sm">{p.name}</td>
                      <td className="text-xs text-white/70">
                        <span className="uppercase text-[10px] tracking-widest font-bold text-[#FBBF24]">{p.scope}</span>
                        {p.scope === "category" && <span className="ml-2 text-white/60">{p.target_slug}</span>}
                        {p.scope === "product" && <span className="ml-2 text-white/60">{(p.target_product_ids || []).length} items</span>}
                      </td>
                      <td className="text-right font-mono-num text-[#FBBF24]">-{fmtPct(p.discount_pct)}</td>
                      <td className="text-[10px] text-white/60">
                        <div>{new Date(p.starts_at).toLocaleString()}</div>
                        <div className="text-white/40">→ {new Date(p.ends_at).toLocaleString()}</div>
                      </td>
                      <td className="text-right">
                        {p.is_live
                          ? <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300" data-testid={`promo-live-${p.id}`}>Live</span>
                          : p.active
                            ? <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">Scheduled</span>
                            : <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 text-white/50">Paused</span>}
                      </td>
                      <td className="text-right space-x-1">
                        <button onClick={() => togglePromo(p)} disabled={busy === p.id} className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest disabled:opacity-40" data-testid={`promo-toggle-${p.id}`}>{p.active ? "Pause" : "Resume"}</button>
                        <button onClick={() => { setEditingPromo(p); setShowPromoForm(true); }} className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-xs" data-testid={`promo-edit-${p.id}`}><Edit2 size={12} /></button>
                        <button onClick={() => delPromo(p)} disabled={busy === p.id} className="bg-[#DC2626] hover:bg-red-700 px-2 py-1 rounded text-xs disabled:opacity-40" data-testid={`promo-del-${p.id}`}><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {section === "cart-shipping" && (
        <CartShippingTiersPanel tiers={cartTiers} onSave={saveCartTiers} busy={busy === "cart-tiers"} />
      )}

      {section === "coupons" && (
        <div className="space-y-3" data-testid="coupons-panel">
          {showCouponForm && (
            <CouponForm
              initial={editingCoupon}
              onSave={saveCoupon}
              onCancel={() => { setShowCouponForm(false); setEditingCoupon(null); }}
              busy={busy === "coupon-save"}
            />
          )}
          <div className="glass-card p-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                    <th className="text-left p-2">Code</th>
                    <th className="text-left">Name</th>
                    <th className="text-right">Discount</th>
                    <th className="text-right">Uses</th>
                    <th className="text-right">Min Cart</th>
                    <th className="text-left">Window</th>
                    <th className="text-right">Live</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-white/40 py-8" data-testid="no-coupons">
                      No coupons yet — create your first code (e.g. <code className="text-[#FBBF24]">WELCOME10</code>).
                    </td></tr>
                  )}
                  {coupons.map((c) => (
                    <tr key={c.id} className="border-t border-white/5" data-testid={`coupon-row-${c.id}`}>
                      <td className="p-2 font-mono-num text-sm font-bold text-[#FBBF24]" data-testid={`coupon-code-${c.id}`}>{c.code}</td>
                      <td className="text-xs text-white/75">{c.name}</td>
                      <td className="text-right font-mono-num text-[#FBBF24]">-{fmtPct(c.discount_pct)}</td>
                      <td className="text-right font-mono-num text-xs">
                        {c.uses_count}{c.max_uses_total ? <span className="text-white/40"> / {c.max_uses_total}</span> : <span className="text-white/40"> / ∞</span>}
                        {c.max_uses_per_user && <div className="text-[9px] text-white/40">{c.max_uses_per_user}/user</div>}
                      </td>
                      <td className="text-right font-mono-num text-xs text-white/60">₱{formatBC(c.min_cart_bc || 0)}</td>
                      <td className="text-[10px] text-white/60">
                        <div>{new Date(c.starts_at).toLocaleString()}</div>
                        <div className="text-white/40">→ {new Date(c.ends_at).toLocaleString()}</div>
                      </td>
                      <td className="text-right">
                        {c.is_live
                          ? <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300" data-testid={`coupon-live-${c.id}`}>Live</span>
                          : c.active
                            ? <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">Scheduled</span>
                            : <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 text-white/50">Paused</span>}
                      </td>
                      <td className="text-right space-x-1">
                        <button onClick={() => toggleCoupon(c)} disabled={busy === c.id} className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest disabled:opacity-40" data-testid={`coupon-toggle-${c.id}`}>{c.active ? "Pause" : "Resume"}</button>
                        <button onClick={() => { setEditingCoupon(c); setShowCouponForm(true); }} className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-xs" data-testid={`coupon-edit-${c.id}`}><Edit2 size={12} /></button>
                        <button onClick={() => delCoupon(c)} disabled={busy === c.id} className="bg-[#DC2626] hover:bg-red-700 px-2 py-1 rounded text-xs disabled:opacity-40" data-testid={`coupon-del-${c.id}`}><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {section === "shop-ui" && (
        <div className="glass-card p-5 space-y-4" data-testid="shop-ui-panel">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Category Visibility</div>
            <div className="text-xs text-white/55 mt-1 max-w-2xl">
              Toggle which categories appear on the public storefront. Products in hidden categories stay in the database (and remain visible in this admin panel) but shoppers won&apos;t see them or the category rail entry.
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="category-visibility-grid">
            {categories.map((c) => {
              const hidden = (shopSettings.hidden_categories || []).includes(c.slug);
              return (
                <label
                  key={c.slug}
                  className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${hidden ? "border-white/10 bg-white/[0.02]" : "border-[#FBBF24]/40 bg-[#FBBF24]/[0.05]"}`}
                  data-testid={`cat-vis-${c.slug}`}
                >
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold truncate ${hidden ? "text-white/50" : "text-white"}`}>{c.label}</div>
                    <div className="text-[10px] text-white/45 font-mono-num">
                      {c.product_count} {c.product_count === 1 ? "product" : "products"}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="accent-[#FBBF24] w-4 h-4 shrink-0"
                    checked={!hidden}
                    disabled={busy === `cat-${c.slug}`}
                    onChange={() => toggleCategoryVisibility(c.slug)}
                    data-testid={`cat-vis-toggle-${c.slug}`}
                  />
                </label>
              );
            })}
          </div>
        </div>
      )}

      {shipFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-5" data-testid="ship-modal">
          <div className="glass-card gold-border-glow p-7 max-w-md w-full">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Mark as Shipped</div>
            <h3 className="font-display text-xl font-bold mt-1 flex items-center gap-2"><Package size={18} /> {shipFor.product_name}</h3>
            <div className="text-xs text-white/55 mt-2">Shipping to: {shipFor.shipping_address}</div>
            <div className="mt-4">
              <label className="zfd-label">Tracking Reference</label>
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="zfd-input" placeholder="e.g. JT123456789PH" data-testid="ship-tracking-input" />
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setShipFor(null)} className="flex-1 bg-white/5 hover:bg-white/10 py-2.5 rounded-md font-semibold text-sm" data-testid="ship-cancel">Cancel</button>
              <button disabled={!tracking.trim()} onClick={ship} className="flex-1 btn-deploy disabled:opacity-50" data-testid="ship-submit">Confirm Ship</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-[#FBBF24] text-[#0F2042] border-[#FBBF24] font-bold"
          : "bg-white/5 text-white/70 border-white/10 hover:border-[#FBBF24]/40"
      }`}
    >
      {children}
    </button>
  );
}


// -----------------------------------------------------------------------------
// PromotionForm — create/edit a store-wide, category, or product-scoped sale.
// -----------------------------------------------------------------------------
function PromotionForm({ initial, categories, products, onSave, onCancel, busy }) {
  const [name, setName] = useState(initial?.name || "");
  const [pct, setPct] = useState(initial ? Math.round(Number(initial.discount_pct) * 100) : 15);
  const [scope, setScope] = useState(initial?.scope || "all");
  const [targetSlug, setTargetSlug] = useState(initial?.target_slug || (categories.find((c) => c.slug !== "uncategorized")?.slug || "uncategorized"));
  const [targetIds, setTargetIds] = useState(initial?.target_product_ids || []);
  const [startsAt, setStartsAt] = useState(initial?.starts_at ? isoToLocalInput(initial.starts_at) : nowLocalInput());
  const [endsAt, setEndsAt] = useState(initial?.ends_at ? isoToLocalInput(initial.ends_at) : plusDaysLocalInput(7));
  const [active, setActive] = useState(initial ? !!initial.active : true);

  const toggleProduct = (id) => {
    setTargetIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Sale name is required.");
    const s = new Date(startsAt); const en = new Date(endsAt);
    if (isNaN(s.getTime()) || isNaN(en.getTime()) || en <= s) return toast.error("Sale end must be after sale start.");
    const p = Number(pct) / 100;
    if (p <= 0 || p > 0.9) return toast.error("Discount must be 1–90%.");
    if (scope === "product" && targetIds.length === 0) return toast.error("Pick at least one product for a product-scoped sale.");
    onSave({
      name: name.trim(),
      discount_pct: p,
      scope,
      target_slug: scope === "category" ? targetSlug : null,
      target_product_ids: scope === "product" ? targetIds : [],
      starts_at: localInputToIso(startsAt),
      ends_at: localInputToIso(endsAt),
      active,
    });
  };

  return (
    <form onSubmit={submit} className="glass-card gold-border p-5 space-y-4" data-testid="promotion-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><Tag size={16} className="text-[#FBBF24]" /> {initial?.id ? "Edit Promotion" : "New Promotion"}</h3>
        <button type="button" onClick={onCancel} className="text-white/60 hover:text-white"><X /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="zfd-label">Sale Name</label>
          <input required className="zfd-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 08.08 Sale, Payday Sale" data-testid="promo-name" />
        </div>
        <div>
          <label className="zfd-label">Discount %</label>
          <input required type="number" min={1} max={90} step={1} className="zfd-input" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="promo-pct" />
        </div>
        <div>
          <label className="zfd-label">Scope</label>
          <select className="zfd-input" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="promo-scope">
            <option value="all">Store-wide (all products)</option>
            <option value="category">Category</option>
            <option value="product">Specific products</option>
          </select>
        </div>
        {scope === "category" && (
          <div>
            <label className="zfd-label">Category</label>
            <select className="zfd-input" value={targetSlug} onChange={(e) => setTargetSlug(e.target.value)} data-testid="promo-target-slug">
              {categories.map((c) => (<option key={c.slug} value={c.slug}>{c.label}</option>))}
            </select>
          </div>
        )}
        <div>
          <label className="zfd-label">Starts</label>
          <input required type="datetime-local" className="zfd-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="promo-start" />
        </div>
        <div>
          <label className="zfd-label">Ends</label>
          <input required type="datetime-local" className="zfd-input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="promo-end" />
        </div>
      </div>
      {scope === "product" && (
        <div className="border border-white/10 rounded-md p-2 bg-black/25 max-h-56 overflow-y-auto" data-testid="promo-target-products">
          <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold mb-2 sticky top-0 bg-black/40 px-1 py-1">
            Products in sale <span className="text-white/50 font-normal normal-case tracking-normal">({targetIds.length} selected)</span>
          </div>
          {products.length === 0 ? (
            <div className="text-white/40 text-xs italic p-2">No products yet — add products first.</div>
          ) : products.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-white/[0.03] cursor-pointer rounded" data-testid={`promo-target-toggle-${p.id}`}>
              <input type="checkbox" checked={targetIds.includes(p.id)} onChange={() => toggleProduct(p.id)} className="accent-[#FBBF24]" />
              <span className="flex-1 truncate">{p.name}</span>
              <span className="text-white/45 font-mono-num">₱{formatBC(p.price_bc)}</span>
            </label>
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#FBBF24]" data-testid="promo-active" />
        <span className="text-white/75">Active — auto-fires within the date window, pauses automatically after end.</span>
      </label>
      <button disabled={busy} type="submit" className="btn-deploy w-full disabled:opacity-50" data-testid="promo-submit">
        {busy ? "Saving…" : initial?.id ? "Save Changes" : "Launch Promotion"}
      </button>
    </form>
  );
}


// -----------------------------------------------------------------------------
// CartShippingTiersPanel — global bulk-shipping ladder applied to any cart.
// -----------------------------------------------------------------------------
function CartShippingTiersPanel({ tiers, onSave, busy }) {
  const [local, setLocal] = useState(() =>
    (tiers || []).map((t) => ({ min_qty: t.min_qty, free: !!t.free, fee_per_item_bc: t.fee_per_item_bc || 0 }))
  );
  useEffect(() => {
    setLocal((tiers || []).map((t) => ({ min_qty: t.min_qty, free: !!t.free, fee_per_item_bc: t.fee_per_item_bc || 0 })));
  }, [tiers]);

  const add = () => setLocal((p) => [...p, { min_qty: (p.at(-1)?.min_qty || 1) + 5, free: false, fee_per_item_bc: 0 }]);
  const upd = (i, patch) => setLocal((p) => p.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const rm = (i) => setLocal((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="glass-card p-5 space-y-4" data-testid="cart-shipping-panel">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold flex items-center gap-2"><Truck size={12} /> Cart-Level Bulk Shipping Tiers</div>
        <div className="text-xs text-white/55 mt-1 max-w-2xl">
          Applies to <strong>every</strong> product at checkout. When a shopper hits the qty threshold, this override
          replaces the product&apos;s base shipping. <strong>Lowest fee wins</strong> vs. per-product tiers.
        </div>
      </div>
      <div className="space-y-2">
        {local.length === 0 && <div className="text-xs text-white/40 italic">No cart-wide tiers. Add one to unlock free/reduced shipping when carts get large.</div>}
        {local.map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-xs bg-black/30 border border-white/5 rounded px-2 py-2" data-testid={`cart-tier-row-${i}`}>
            <span className="text-white/50 shrink-0">Cart qty ≥</span>
            <input type="number" min={1} className="zfd-input w-24" value={t.min_qty} onChange={(e) => upd(i, { min_qty: e.target.value })} data-testid={`cart-tier-qty-${i}`} />
            <span className="text-white/50">→</span>
            <label className="flex items-center gap-1 text-white/70">
              <input type="checkbox" className="accent-[#FBBF24]" checked={!!t.free} onChange={(e) => upd(i, { free: e.target.checked })} data-testid={`cart-tier-free-${i}`} /> Free
            </label>
            {!t.free && (
              <>
                <span className="text-white/50">or ₱</span>
                <input type="number" min={0} step="0.01" className="zfd-input w-28" value={t.fee_per_item_bc} onChange={(e) => upd(i, { fee_per_item_bc: e.target.value })} data-testid={`cart-tier-fee-${i}`} />
                <span className="text-white/50">/item</span>
              </>
            )}
            <button type="button" onClick={() => rm(i)} className="ml-auto text-[#FCA5A5] hover:text-white bg-white/5 hover:bg-[#DC2626] px-1.5 py-1 rounded" data-testid={`cart-tier-rm-${i}`}><X size={12} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={add} className="text-xs px-3 py-2 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1" data-testid="cart-tier-add"><Plus size={12} /> Add tier</button>
        <button type="button" onClick={() => onSave(local)} disabled={busy} className="btn-deploy text-xs disabled:opacity-50" data-testid="cart-tier-save">{busy ? "Saving…" : "Save tiers"}</button>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// CouponForm — create/edit an admin-managed public coupon code.
// -----------------------------------------------------------------------------
function CouponForm({ initial, onSave, onCancel, busy }) {
  const [code, setCode] = useState(initial?.code || "");
  const [name, setName] = useState(initial?.name || "");
  const [pct, setPct] = useState(initial ? Math.round(Number(initial.discount_pct) * 100) : 10);
  const [startsAt, setStartsAt] = useState(initial?.starts_at ? isoToLocalInput(initial.starts_at) : nowLocalInput());
  const [endsAt, setEndsAt] = useState(initial?.ends_at ? isoToLocalInput(initial.ends_at) : plusDaysLocalInput(30));
  const [active, setActive] = useState(initial ? !!initial.active : true);
  const [maxTotal, setMaxTotal] = useState(initial?.max_uses_total ?? "");
  const [maxPerUser, setMaxPerUser] = useState(initial?.max_uses_per_user ?? 1);
  const [minCart, setMinCart] = useState(initial?.min_cart_bc ?? 0);

  const submit = (e) => {
    e.preventDefault();
    const codeNorm = (code || "").trim().toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9\-_]{1,31}$/.test(codeNorm)) return toast.error("Code must be 2–32 chars, A–Z / 0–9 / dash / underscore.");
    if (!name.trim()) return toast.error("Coupon name is required.");
    const s = new Date(startsAt); const en = new Date(endsAt);
    if (isNaN(s.getTime()) || isNaN(en.getTime()) || en <= s) return toast.error("End must be after start.");
    const p = Number(pct) / 100;
    if (p <= 0 || p > 0.9) return toast.error("Discount must be 1–90%.");
    onSave({
      code: codeNorm,
      name: name.trim(),
      discount_pct: p,
      starts_at: localInputToIso(startsAt),
      ends_at: localInputToIso(endsAt),
      active,
      max_uses_total: maxTotal === "" || maxTotal === null ? null : Math.max(1, parseInt(maxTotal, 10)),
      max_uses_per_user: maxPerUser === "" || maxPerUser === null ? null : Math.max(1, parseInt(maxPerUser, 10)),
      min_cart_bc: Math.max(0, Number(minCart) || 0),
    });
  };

  return (
    <form onSubmit={submit} className="glass-card gold-border p-5 space-y-4" data-testid="coupon-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><Ticket size={16} className="text-[#FBBF24]" /> {initial?.id ? "Edit Coupon" : "New Coupon"}</h3>
        <button type="button" onClick={onCancel} className="text-white/60 hover:text-white"><X /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="zfd-label">Code (uppercase auto)</label>
          <input required className="zfd-input font-mono-num tracking-widest" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="coupon-code" />
        </div>
        <div>
          <label className="zfd-label">Description</label>
          <input required className="zfd-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome bonus 10% off" data-testid="coupon-name" />
        </div>
        <div>
          <label className="zfd-label">Discount % <span className="text-white/40">(hard-capped at 30% of subtotal)</span></label>
          <input required type="number" min={1} max={90} step={1} className="zfd-input" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="coupon-pct" />
        </div>
        <div>
          <label className="zfd-label">Min Cart (BC) <span className="text-white/40">(after sale)</span></label>
          <input type="number" min={0} step="0.01" className="zfd-input" value={minCart} onChange={(e) => setMinCart(e.target.value)} data-testid="coupon-min-cart" />
        </div>
        <div>
          <label className="zfd-label">Starts</label>
          <input required type="datetime-local" className="zfd-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="coupon-start" />
        </div>
        <div>
          <label className="zfd-label">Ends</label>
          <input required type="datetime-local" className="zfd-input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="coupon-end" />
        </div>
        <div>
          <label className="zfd-label">Max Uses (total) <span className="text-white/40">(blank = ∞)</span></label>
          <input type="number" min={1} className="zfd-input" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} placeholder="e.g. 500" data-testid="coupon-max-total" />
        </div>
        <div>
          <label className="zfd-label">Max Uses per user <span className="text-white/40">(blank = ∞)</span></label>
          <input type="number" min={1} className="zfd-input" value={maxPerUser} onChange={(e) => setMaxPerUser(e.target.value)} placeholder="1" data-testid="coupon-max-per-user" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#FBBF24]" data-testid="coupon-active" />
        <span className="text-white/75">Active — automatically fires within the date window, pauses when the window closes.</span>
      </label>
      <button disabled={busy} type="submit" className="btn-deploy w-full disabled:opacity-50" data-testid="coupon-submit">
        {busy ? "Saving…" : initial?.id ? "Save Changes" : "Launch Coupon"}
      </button>
    </form>
  );
}

